import { GripVertical, Layers, Plus, Sparkles, Square, Trash2, Upload, WandSparkles } from 'lucide-react'
import { useEffect, useState, type DragEvent } from 'react'
import { JellyGenerateButton } from './JellyGenerateButton'
import { RECOMMENDED_UPSCALE_MODEL, RECOMMENDED_UPSCALE_MODEL_URL } from './defaults'
import { resolveModelName } from './lib/modelNames'
import { ENHANCE_LONG_EDGE_MAX, ENHANCE_LONG_EDGE_MIN, enhanceInterpolationNodes, enhanceOutputSize, normalizeLongEdge, selectConcatBackend } from './lib/upscaleWorkflow'
import type { MediaSize } from './lib/mediaTracks'
import type { ModelLists, UpscaleSettings, UpscaleTool, WorkspaceMode } from './types'

export function WorkspaceModeTabs({ mode, onMode }: { mode: WorkspaceMode; onMode: (mode: WorkspaceMode) => void }) {
  const modes = [
    { id: 'multi', label: 'Multi', shortLabel: 'M', icon: Layers },
    { id: 'simple', label: 'Simple', shortLabel: 'S', icon: Square },
    { id: 'upscale', label: 'Upscale', shortLabel: 'UP', icon: Sparkles },
    { id: 'seedance', label: 'Seedance', shortLabel: 'SD', icon: WandSparkles },
  ] as const

  return (
    <div className="segmented workspace-mode-tabs" aria-label="Workspace mode">
      {modes.map((item) => {
        const Icon = item.icon
        const active = mode === item.id
        return (
          <button
            key={item.id}
            type="button"
            className={active ? 'is-active' : ''}
            aria-label={item.label}
            aria-pressed={active}
            disabled={item.id === 'seedance'}
            title={item.id === 'seedance' ? '準備中' : undefined}
            onClick={() => onMode(item.id)}
          >
            <span className="workspace-mode-full"><Icon size={14} aria-hidden="true" /><span>{item.label}</span></span>
            <span className="workspace-mode-short" aria-hidden="true">{item.shortLabel}</span>
          </button>
        )
      })}
    </div>
  )
}

type UpscaleQuickPanelProps = {
  mode: WorkspaceMode
  tool: UpscaleTool
  settings: UpscaleSettings
  sourceSize?: MediaSize | null
  models: ModelLists
  connected: boolean
  submitting: boolean
  batch: number
  onMode: (mode: WorkspaceMode) => void
  onTool: (tool: UpscaleTool) => void
  onSettings: (patch: Partial<UpscaleSettings>) => void
  onBatch: (value: number) => void
  onGenerate: () => void
}

const numeric = (value: string, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function UpscaleQuickPanel(props: UpscaleQuickPanelProps) {
  const upscalers = props.models.upscale ?? []
  const resolvedUpscaler = resolveModelName(props.settings.upscaleModel, upscalers)
  const selectedUpscaler = resolvedUpscaler ?? (props.connected ? '' : props.settings.upscaleModel)
  const hasUpscaleNodes = ['UpscaleModelLoader', 'ImageUpscaleWithModel', 'ImageScaleBy']
    .every((nodeType) => props.models.nodes?.includes(nodeType))
  const noUpscaleModel = props.connected && upscalers.length === 0
  const missingUpscaleNodes = props.connected && !hasUpscaleNodes
  const enhanceUnavailable = props.tool === 'enhance' && (noUpscaleModel || missingUpscaleNodes)
  const longEdgeMode = props.settings.scaleMode === 'longEdge'
  const outputSize = enhanceOutputSize(props.sourceSize ?? null, props.settings)
  const gimmUnavailable = props.connected && !enhanceInterpolationNodes('gimm').every((nodeType) => props.models.nodes?.includes(nodeType))
  useEffect(() => {
    if (!props.connected || !upscalers.length) return
    const nextModel = resolveModelName(props.settings.upscaleModel, upscalers) ?? upscalers[0]
    if (nextModel !== props.settings.upscaleModel) props.onSettings({ upscaleModel: nextModel })
  }, [props.connected, props.onSettings, props.settings.upscaleModel, upscalers])
  return (
    <aside className="quick-panel upscale-quick-panel" aria-label="Upscale controls">
      <div className="quick-fixed-top">
        <WorkspaceModeTabs mode={props.mode} onMode={props.onMode} />
        <section className="section">
          <div className="section-title"><span>Upscale</span><small>処理を選択</small></div>
          <div className="segmented upscale-tool-tabs" aria-label="Upscale tool">
            {(['enhance', 'concat'] as const).map((tool) => <button
              key={tool}
              type="button"
              className={props.tool === tool ? 'is-active' : ''}
              aria-pressed={props.tool === tool}
              onClick={() => props.onTool(tool)}
            >{tool === 'enhance' ? 'Enhance' : 'Concat'}</button>)}
          </div>
        </section>
      </div>
      <div className="quick-scroll">
        {props.tool === 'enhance' ? <section className="section field-stack">
          <div className="section-title"><span>Enhance Settings</span><small>画像 / MP4</small></div>
          <label><span>Upscale Model</span><select value={selectedUpscaler} disabled={noUpscaleModel} onChange={(event) => props.onSettings({ upscaleModel: event.target.value })}>
            {upscalers.length
              ? upscalers.map((model) => <option key={model}>{model}</option>)
              : <option value="">{props.connected ? 'Upscale Model がインストールされていません' : props.settings.upscaleModel}</option>}
          </select></label>
          <div className="enhance-source-field"><span>Final Scale</span>
            <div className="final-scale-row">
              <div className="segmented upscale-tool-tabs" aria-label="Final scale mode">
                {(['factor', 'longEdge'] as const).map((scaleMode) => <button
                  key={scaleMode}
                  type="button"
                  className={props.settings.scaleMode === scaleMode ? 'is-active' : ''}
                  aria-pressed={props.settings.scaleMode === scaleMode}
                  onClick={() => props.onSettings({ scaleMode })}
                >{scaleMode === 'factor' ? '倍率' : '長辺'}</button>)}
              </div>
              {longEdgeMode
                ? <span className="final-scale-px"><input type="number" aria-label="Long edge (px)" min={ENHANCE_LONG_EDGE_MIN} max={ENHANCE_LONG_EDGE_MAX} step="2" value={props.settings.longEdge} onChange={(event) => props.onSettings({ longEdge: Math.max(1, Math.min(ENHANCE_LONG_EDGE_MAX, Math.round(numeric(event.target.value, 1920)))) })} onBlur={() => props.onSettings({ longEdge: normalizeLongEdge(props.settings.longEdge) })} /><small>px</small></span>
                : <input type="number" aria-label="Scale factor" min="1" max="4" step="0.25" value={props.settings.scale} onChange={(event) => props.onSettings({ scale: Math.max(1, Math.min(4, numeric(event.target.value, 2))) })} />}
            </div>
            {props.sourceSize && outputSize && <small className="final-scale-result">{props.sourceSize.width}×{props.sourceSize.height} → <strong>{outputSize.width}×{outputSize.height}</strong>{longEdgeMode ? `（×${outputSize.factor.toFixed(2)}）` : ''}</small>}
          </div>
          <label><span>Target FPS</span><input type="number" min="1" max="120" step="1" value={props.settings.targetFps} onChange={(event) => props.onSettings({ targetFps: Math.max(1, Math.min(120, numeric(event.target.value, 48))) })} /></label>
          <div className="enhance-source-field"><span>Source</span>
            <div className="segmented upscale-tool-tabs" aria-label="Enhance source">
              {(['wan', 'h3'] as const).map((source) => <button
                key={source}
                type="button"
                className={props.settings.enhanceSource === source ? 'is-active' : ''}
                aria-pressed={props.settings.enhanceSource === source}
                onClick={() => props.onSettings({ enhanceSource: source })}
              >{source === 'wan' ? 'WAN' : 'H3'}</button>)}
            </div>
          </div>
          {props.settings.enhanceSource === 'h3' && gimmUnavailable && <p className="warning-text">接続中の Server に GIMM-VFI node が見つかりません。</p>}
          <div className="switch-row"><label><input type="checkbox" checked={props.settings.preserveAudio} onChange={(event) => props.onSettings({ preserveAudio: event.target.checked })} /> 音声を保持</label><label><input type="checkbox" checked={props.settings.enhanceLoop} onChange={(event) => props.onSettings({ enhanceLoop: event.target.checked })} /> ループ補正</label></div>
          <p className="hint">画像では補間 / FPS / Audio、無音の MP4 では Audio を自動的にスキップします。Scale は最終的な倍率です。Source は MP4 の FPS から自動で切り替わります（WAN: 16 fps / RIFE、H3: 24 fps / GIMM-VFI）。ループ補正では終端から先頭へのつなぎ目も補間します。</p>
          {noUpscaleModel && <div className="warning-text model-install-help">
            <span>接続中の Server に Upscale Model がありません。</span>
            <a href={RECOMMENDED_UPSCALE_MODEL_URL} target="_blank" rel="noreferrer">
              {RECOMMENDED_UPSCALE_MODEL} をダウンロード ↗
            </a>
            <small>models/upscale_models に配置し、ComfyUI を再起動してください。</small>
          </div>}
          {!noUpscaleModel && missingUpscaleNodes && <p className="warning-text">接続中の Server に必要な Upscale node が見つかりません。</p>}
        </section> : <section className="section field-stack">
          <div className="section-title"><span>Concat Settings</span><small>2–8 videos</small></div>
          <label><span>Output FPS</span><input type="number" min="1" max="120" value={props.settings.concatFps} onChange={(event) => props.onSettings({ concatFps: Math.max(1, Math.min(120, numeric(event.target.value, 24))) })} /></label>
          <label><span>Quality CRF</span><input type="number" min="0" max="51" value={props.settings.concatCrf} onChange={(event) => props.onSettings({ concatCrf: Math.max(0, Math.min(51, numeric(event.target.value, 18))) })} /></label>
          <div className="fixed-setting"><span>Canvas</span><strong>First Clip</strong></div>
          <div className="fixed-setting"><span>Fit</span><strong>Contain / Padding</strong></div>
          <p className="hint">現在の FFmpeg workflow は映像のみを結合します。音声は出力に含まれません。</p>
          {props.connected && !selectConcatBackend(props.models.nodes ?? []) && <p className="warning-text">接続中の Server に Mugen Concat node または互換 node が見つかりません。</p>}
        </section>}
      </div>
      <div className="quick-fixed-bottom">
        <div className="generate-row">
          <JellyGenerateButton onClick={props.onGenerate} disabled={props.submitting || enhanceUnavailable}><Sparkles size={16} /> {props.submitting ? 'Submitting…' : 'Generate'}</JellyGenerateButton>
          <label className="batch-field"><span>Batch</span><input type="number" min="1" max="99" value={props.batch} onChange={(event) => props.onBatch(Math.max(1, Math.min(99, numeric(event.target.value, 1))))} /></label>
        </div>
      </div>
    </aside>
  )
}

function useFileUrl(file: File | null): string {
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (!file) { setUrl(''); return }
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [file])
  return url
}

function SourcePreview({ file }: { file: File }) {
  const url = useFileUrl(file)
  if (!url) return null
  return file.type.startsWith('image/')
    ? <img src={url} alt={file.name} />
    : <video src={url} aria-label={file.name} autoPlay loop muted playsInline />
}

type UpscaleCenterProps = {
  tool: UpscaleTool
  enhanceFile: File | null
  concatFiles: Array<File | null>
  onEnhanceFile: (file: File | null) => void
  onConcatFiles: (files: Array<File | null>) => void
}

const acceptedEnhance = (file?: File): file is File => Boolean(file && (file.type.startsWith('image/') || file.type === 'video/mp4' || /\.mp4$/i.test(file.name)))
const acceptedVideo = (file?: File): file is File => Boolean(file && (file.type === 'video/mp4' || /\.mp4$/i.test(file.name)))

export function UpscaleCenter(props: UpscaleCenterProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const setConcat = (index: number, file: File | null) => props.onConcatFiles(props.concatFiles.map((current, currentIndex) => currentIndex === index ? file : current))
  const dropConcat = (event: DragEvent<HTMLElement>, index: number) => {
    event.preventDefault()
    const droppedFile = event.dataTransfer.files[0]
    if (acceptedVideo(droppedFile)) { setConcat(index, droppedFile); setDragIndex(null); return }
    if (dragIndex === null || dragIndex === index) return
    const next = [...props.concatFiles]
    const [moved] = next.splice(dragIndex, 1)
    next.splice(index, 0, moved)
    props.onConcatFiles(next)
    setDragIndex(null)
  }
  return (
    <section className="upscale-center" aria-label="Upscale source workspace">
      <div className="timeline-toolbar upscale-toolbar">
        <div className="timeline-title"><h2>{props.tool === 'enhance' ? 'Enhance Source' : 'Concat Sources'}</h2><span>{props.tool === 'enhance' ? '画像または MP4 を 1 件選択' : 'MP4 をドラッグして順番を調整・2～8 本'}</span></div>
      </div>
      {props.tool === 'enhance' ? <div className="enhance-source-wrap">
        <label className={`media-source-card ${props.enhanceFile ? 'has-media' : ''}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); const file = event.dataTransfer.files[0]; if (acceptedEnhance(file)) props.onEnhanceFile(file) }}>
          <input type="file" accept="image/*,video/mp4" hidden onChange={(event) => { const file = event.target.files?.[0]; if (acceptedEnhance(file)) props.onEnhanceFile(file); event.currentTarget.value = '' }} />
          {props.enhanceFile ? <><SourcePreview file={props.enhanceFile} /><span className="media-file-name">{props.enhanceFile.name}</span></> : <span className="media-drop-copy"><Upload size={24} /><strong>画像 / MP4 を選択</strong><small>クリックまたはドロップ</small></span>}
        </label>
        {props.enhanceFile && <button className="icon-button danger source-remove" type="button" aria-label="Remove Enhance source" onClick={() => props.onEnhanceFile(null)}><Trash2 size={16} /></button>}
      </div> : <div className="concat-grid">
        {props.concatFiles.map((file, index) => <article
          className={`concat-slot ${file ? 'has-media' : ''}`}
          key={index}
          draggable={Boolean(file)}
          onDragStart={() => setDragIndex(index)}
          onDragEnd={() => setDragIndex(null)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => dropConcat(event, index)}
        >
          <header><span><GripVertical size={15} /> #{index + 1}</span>{file && <button className="icon-button danger" type="button" aria-label={`Remove Concat source ${index + 1}`} onClick={() => setConcat(index, null)}><Trash2 size={14} /></button>}</header>
          <label className="concat-upload">
            <input type="file" accept="video/mp4" hidden onChange={(event) => { const next = event.target.files?.[0]; if (acceptedVideo(next)) setConcat(index, next); event.currentTarget.value = '' }} />
            {file ? <><SourcePreview file={file} /><span className="media-file-name">{file.name}</span></> : <span className="media-drop-copy"><Upload size={22} /><strong>MP4 を選択</strong><small>クリックまたはドロップ</small></span>}
          </label>
        </article>)}
        {props.concatFiles.length < 8 && <button className="concat-add" type="button" onClick={() => props.onConcatFiles([...props.concatFiles, null])}><Plus size={22} /><span>Add video</span><small>{props.concatFiles.length} / 8</small></button>}
      </div>}
    </section>
  )
}
